/**
 * Station stock bookkeeping (spec §5.1-§5.3): the small shared helpers every economy task uses. All
 * allocation-free (D-014). Per station-ware values live at `sw(s, w)`.
 *
 * - `available` = stock − reserved: what a buyer can still take (reservations raise the price others see).
 * - `sellable` = available − `yardHeldBill`: what a station gives up to any other buyer or taker (M10 P4a7e: a yard
 *   keeps its aged orders' bill back from sale).
 * - `room` = capacity − stock: what a seller can still deliver. Incoming is not a reservation; scoring uses
 *   the projected fill `(stock + incoming) / capacity` (`economy/prices.ts`).
 * - `addStock` / `takeStock` clamp to what fits, return the amount moved, and keep the day's fill extremes
 *   (`dayFillMin` / `dayFillMax`, reset by the day rollup) up to date.
 */
import { ORDER_STATE, TRADE_BIT, type StationStore, type StrategicState } from '../types'
import { WARE_COUNT } from '../wares'

/** Index of station `s`, ware `w` in the per station-ware arrays. */
export function sw(s: number, w: number): number {
  return s * WARE_COUNT + w
}

function fillAt(st: StationStore, k: number): number {
  const cap = st.cap[k]
  if (!(cap > 0)) return 0
  const f = st.stock[k] / cap
  return f < 1 ? (f > 0 ? f : 0) : 1
}

/** stock / capacity clamped to 0..1 (0 when the station has no capacity for the ware). */
export function fillOf(st: StationStore, s: number, w: number): number {
  return fillAt(st, s * WARE_COUNT + w)
}

/** Stock a buyer can take: stock − reserved, never below 0. */
export function available(st: StationStore, s: number, w: number): number {
  const k = s * WARE_COUNT + w
  const a = st.stock[k] - st.reserved[k]
  return a > 0 ? a : 0
}

/**
 * M10 P4a7e (D-TBD): the tonnes of ware `w` yard `s` withholds from sale for its aged orders: the largest cost of `w`
 * among its queued orders that have waited `yardQueue.ownerFirstMaxAgeDays` (the rule of SUP-1b's `heldNeed` aged
 * part), capped at the yard's capacity. 0 at a non-yard, with an empty queue, or with `yardQueue.enabled` or
 * `yardQueue.withholdAgedBill` off. Derived from the saved order book on demand: no state, allocation-free.
 * The start check (`shipyard.ts componentsReady`, stock − reserved) does not read it: the bill is held for the order.
 */
export function yardHeldBill(state: StrategicState, s: number, w: number): number {
  const st = state.stations
  if (st.yardSlots[s] === 0 || st.orderHead[s] < 0) return 0
  const queue = state.data.hulls.yardQueue
  if (!queue.enabled || !queue.withholdAgedBill) return 0
  const o = state.orders
  const cost = state.data.hullCost
  const maxAge = queue.ownerFirstMaxAgeDays * state.data.strategic.clock.dayS
  let held = 0
  for (let i = st.orderHead[s]; i >= 0; i = o.next[i]) {
    if (o.state[i] !== ORDER_STATE.queued || state.time - o.placedAt[i] < maxAge) continue
    const q = cost[o.hull[i] * WARE_COUNT + w]
    if (q > held) held = q
  }
  const cap = st.cap[s * WARE_COUNT + w]
  return held < cap ? held : cap
}

/** Stock station `s` gives up to any buyer or taker: `available` less `yardHeldBill`, never below 0 (M10 P4a7e). */
export function sellable(state: StrategicState, s: number, w: number): number {
  const a = available(state.stations, s, w) - yardHeldBill(state, s, w)
  return a > 0 ? a : 0
}

/** Space a seller can fill: capacity − stock, never below 0. */
export function room(st: StationStore, s: number, w: number): number {
  const k = s * WARE_COUNT + w
  const r = st.cap[k] - st.stock[k]
  return r > 0 ? r : 0
}

export function buys(st: StationStore, s: number, w: number): boolean {
  return (st.trade[s * WARE_COUNT + w] & TRADE_BIT.buys) !== 0
}

export function sells(st: StationStore, s: number, w: number): boolean {
  return (st.trade[s * WARE_COUNT + w] & TRADE_BIT.sells) !== 0
}

function trackFill(st: StationStore, k: number): void {
  const f = fillAt(st, k)
  if (f < st.dayFillMin[k]) st.dayFillMin[k] = f
  if (f > st.dayFillMax[k]) st.dayFillMax[k] = f
}

/** Adds up to `q` t (clamped to room). Returns the tonnes added. */
export function addStock(st: StationStore, s: number, w: number, q: number): number {
  if (!(q > 0)) return 0
  const k = s * WARE_COUNT + w
  const r = st.cap[k] - st.stock[k]
  const add = q < r ? q : r > 0 ? r : 0
  if (add > 0) {
    st.stock[k] += add
    trackFill(st, k)
  }
  return add
}

/**
 * Removes up to `q` t (clamped to stock, reserved tonnes included: a buyer taking its own reservation first
 * `unreserve`s it). Returns the tonnes removed.
 */
export function takeStock(st: StationStore, s: number, w: number, q: number): number {
  if (!(q > 0)) return 0
  const k = s * WARE_COUNT + w
  const have = st.stock[k]
  const take = q < have ? q : have > 0 ? have : 0
  if (take > 0) {
    st.stock[k] = have - take
    if (st.reserved[k] > st.stock[k]) st.reserved[k] = st.stock[k]
    trackFill(st, k)
  }
  return take
}

/** Reserves up to `q` t of the available stock. Returns the tonnes reserved. */
export function reserve(st: StationStore, s: number, w: number, q: number): number {
  if (!(q > 0)) return 0
  const k = s * WARE_COUNT + w
  const a = st.stock[k] - st.reserved[k]
  const r = q < a ? q : a > 0 ? a : 0
  st.reserved[k] += r
  return r
}

/** Releases `q` t of reservations (never below 0). */
export function unreserve(st: StationStore, s: number, w: number, q: number): void {
  if (!(q > 0)) return
  const k = s * WARE_COUNT + w
  const r = st.reserved[k] - q
  st.reserved[k] = r > 0 ? r : 0
}

/** Adds `q` t to the station's incoming (committed sales on their way). */
export function addIncoming(st: StationStore, s: number, w: number, q: number): void {
  if (!(q > 0)) return
  st.incoming[s * WARE_COUNT + w] += q
}

/** Removes `q` t from the station's incoming (never below 0). */
export function clearIncoming(st: StationStore, s: number, w: number, q: number): void {
  if (!(q > 0)) return
  const k = s * WARE_COUNT + w
  const r = st.incoming[k] - q
  st.incoming[k] = r > 0 ? r : 0
}

/** Starts a new day's fill extremes at the current fill (called by the day rollup for every station-ware). */
export function resetDayFill(st: StationStore, s: number, w: number): void {
  const k = s * WARE_COUNT + w
  const f = fillAt(st, k)
  st.dayFillMin[k] = f
  st.dayFillMax[k] = f
}
